import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { closingAdjustmentActions } from '../actions/closing-adjustment.actions';
import {
    IAdjustmentTransferResponse,
    ICreateAdjustmentRequest,
    IEditAdjustmentRequest,
} from '../models/closing-adjustment.model';
import {
    selectAccountAdjustmentOpenCount,
    selectAccountAdjustmentReadOnly,
    selectAccountAdjustmentRows,
    selectAccountAdjustmentSelectedYear,
    selectAccountAdjustmentYears,
    selectAccountAdjustmentsError,
    selectAccountAdjustmentsLoading,
    selectAdjustmentActiveFilter,
    selectAdjustmentCandidates,
    selectAdjustmentCounts,
    selectAdjustmentDetail,
    selectAdjustmentDetailError,
    selectAdjustmentDetailLoading,
    selectAdjustmentBalanceSheetDate,
    selectAdjustmentFiscalYears,
    selectAdjustmentLastTransfer,
    selectAdjustmentReadOnly,
    selectAdjustmentResult,
    selectAdjustmentRows,
    selectAdjustmentSearchHits,
    selectAdjustmentSearchTerm,
    selectAdjustmentSelectedYear,
    selectAdjustmentSort,
    selectAdjustmentTransferOptions,
    selectAdjustmentWriteErrorKey,
    selectAdjustmentWritePending,
    selectAdjustmentsError,
    selectAdjustmentsLoading,
    selectTransferableAdjustmentIds,
} from '../selectors/closing-adjustment.selectors';
import {
    AdjustmentCandidate,
    AdjustmentFilterCounts,
    AdjustmentFilterKey,
    AdjustmentResultRows,
    AdjustmentRow,
    AdjustmentSort,
    AdjustmentTransferMethod,
    AdjustmentTransferOptions,
} from '../types/closing-adjustments.types';

/**
 * Facade for the ABCP correction-booking slice — REV-21375 (ADR-0015).
 *
 * Pages consume THIS and never reach into the Store directly, the same rule `ClosingDocumentProvider`
 * follows. Every getter returns exactly one `@Input()` of a pure view, so a host template binds through
 * the async pipe without mapping anything.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ **WHAT THIS FACADE CANNOT DO, and that is the point** (ADR-0015 §2 / §7 / §8):
 *
 *   - it cannot set a state. There is no `markAsBooked`, and there must not be — `Gebucht` comes out of
 *     the imported ERP journal. {@link resolveAmbiguity} is the single exception, and it names WHICH of
 *     the candidates the reconciliation already found is the proof;
 *   - it cannot delete a correction. `discard` is a state, and the naming is deliberate;
 *   - it cannot accept or reject a proposal. The agent proposes none in this flow, so there is nothing
 *     to accept — and a method nobody can exercise is a method somebody eventually calls.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */
@Injectable({ providedIn: 'root' })
export class ClosingAdjustmentProvider {
    public constructor(private readonly store: Store) {}

    // ─── Reads · the closing-wide register ───────────────────────────────────────────────────────

    public getAdjustments$(): Observable<AdjustmentRow[]> {
        return this.store.select(selectAdjustmentRows);
    }

    public getCounts$(): Observable<AdjustmentFilterCounts> {
        return this.store.select(selectAdjustmentCounts);
    }

    public getActiveFilter$(): Observable<AdjustmentFilterKey> {
        return this.store.select(selectAdjustmentActiveFilter);
    }

    public getSort$(): Observable<AdjustmentSort | null> {
        return this.store.select(selectAdjustmentSort);
    }

    public getSearchTerm$(): Observable<string> {
        return this.store.select(selectAdjustmentSearchTerm);
    }

    /** How many rows the search matched. `null` = nothing is being searched — a different state. */
    public getSearchHits$(): Observable<number | null> {
        return this.store.select(selectAdjustmentSearchHits);
    }

    public getFiscalYears$(): Observable<number[]> {
        return this.store.select(selectAdjustmentFiscalYears);
    }

    public getSelectedFiscalYear$(): Observable<number | null> {
        return this.store.select(selectAdjustmentSelectedYear);
    }

    /** A PRIOR year — the whole surface is read-only (§10). The SERVER's answer. */
    public getReadOnly$(): Observable<boolean> {
        return this.store.select(selectAdjustmentReadOnly);
    }

    /** The three derived figures plus the list total. Computed by the backend (§11). */
    public getResult$(): Observable<AdjustmentResultRows | null> {
        return this.store.select(selectAdjustmentResult);
    }

    /** What the transfer control may offer — QUERIED per mandate, never a constant (§3a). */
    public getTransferOptions$(): Observable<AdjustmentTransferOptions> {
        return this.store.select(selectAdjustmentTransferOptions);
    }

    public getLoading$(): Observable<boolean> {
        return this.store.select(selectAdjustmentsLoading);
    }

    public getError$(): Observable<boolean> {
        return this.store.select(selectAdjustmentsError);
    }

    /**
     * The number the closing's tab badge shows: how many corrections are still OPEN (AC1).
     *
     * From the same counts the `Offen` pill reads, so the badge and the pill are one number by
     * construction. It is the number by which a user sees whether work is still waiting here — and the
     * reason the shell loads this slice on every tab, not only on the one that renders the list.
     */
    public getOpenCount$(): Observable<number> {
        return this.store.select(selectAdjustmentCounts).pipe(map((counts) => counts.released));
    }

    /**
     * EVERY non-discarded correction of the year — what the tab's counter pill shows.
     *
     * Deliberately not `open`: the pill answers "how much is in here", which is the question a tab
     * counter asks everywhere else in this header. "How much is still to do" is what the `Offen` pill
     * inside the register answers, and having the tab say one thing while the pill under it says
     * another was the confusing part.
     */
    public getTotalCount$(): Observable<number> {
        return this.store.select(selectAdjustmentCounts).pipe(map((counts) => counts.all));
    }

    // ─── Reads · the IN-ACCOUNT list (REV-21364) ─────────────────────────────────────────────────

    /** Load this ACCOUNT's corrections. `fiscalYear: null` = the current one, which the server picks. */
    public loadAccountAdjustments(projectId: string, accountId: string, fiscalYear: number | null = null): void {
        this.store.dispatch(closingAdjustmentActions.loadAccountAdjustments({ projectId, accountId, fiscalYear }));
    }

    /** Dropped on leaving the account, so the next one does not open on the previous one's rows. */
    public clearAccountAdjustments(): void {
        this.store.dispatch(closingAdjustmentActions.clearAccountAdjustments());
    }

    public getAccountAdjustments$(): Observable<AdjustmentRow[]> {
        return this.store.select(selectAccountAdjustmentRows);
    }

    public getAccountAdjustmentYears$(): Observable<number[]> {
        return this.store.select(selectAccountAdjustmentYears);
    }

    public getAccountAdjustmentSelectedYear$(): Observable<number | null> {
        return this.store.select(selectAccountAdjustmentSelectedYear);
    }

    public getAccountAdjustmentReadOnly$(): Observable<boolean> {
        return this.store.select(selectAccountAdjustmentReadOnly);
    }

    public getAccountAdjustmentOpenCount$(): Observable<number> {
        return this.store.select(selectAccountAdjustmentOpenCount);
    }

    public getAccountAdjustmentsLoading$(): Observable<boolean> {
        return this.store.select(selectAccountAdjustmentsLoading);
    }

    public getAccountAdjustmentsError$(): Observable<boolean> {
        return this.store.select(selectAccountAdjustmentsError);
    }

    // ─── Reads · the detail surface ──────────────────────────────────────────────────────────────

    public getAdjustment$(): Observable<AdjustmentRow | null> {
        return this.store.select(selectAdjustmentDetail);
    }

    /** The lines the reconciliation FOUND — non-empty exactly when the correction is ambiguous (§4). */
    public getCandidates$(): Observable<AdjustmentCandidate[]> {
        return this.store.select(selectAdjustmentCandidates);
    }

    public getDetailLoading$(): Observable<boolean> {
        return this.store.select(selectAdjustmentDetailLoading);
    }

    public getDetailError$(): Observable<boolean> {
        return this.store.select(selectAdjustmentDetailError);
    }

    // ─── Reads · writes in flight ───────────────────────────────────────────────────────────────

    /** The closing's balance-sheet date, for the entry dialog's default booking date (V2/AC3). */
    public getBalanceSheetDate$(): Observable<string | null> {
        return this.store.select(selectAdjustmentBalanceSheetDate);
    }

    public getWritePending$(): Observable<boolean> {
        return this.store.select(selectAdjustmentWritePending);
    }

    /** A refusal, as a translation KEY — never server prose (AC13). */
    public getWriteErrorKey$(): Observable<string | null> {
        return this.store.select(selectAdjustmentWriteErrorKey);
    }

    /** The last transfer's outcome: count, total and the per-position result. */
    public getLastTransfer$(): Observable<IAdjustmentTransferResponse | null> {
        return this.store.select(selectAdjustmentLastTransfer);
    }

    /** The ids `Übertragen (n)` would send: every OPEN correction of the current year. */
    public getTransferableIds$(): Observable<string[]> {
        return this.store.select(selectTransferableAdjustmentIds);
    }

    // ─── Writes ──────────────────────────────────────────────────────────────────────────────────

    /** First load of a closing. `fiscalYear: null` = the current one, which the SERVER decides. */
    public loadAdjustments(projectId: string, fiscalYear: number | null = null): void {
        this.store.dispatch(closingAdjustmentActions.loadAdjustments({ projectId, fiscalYear, filter: 'all' }));
    }

    /** Re-ask for the CURRENT year and pill. What a caller uses when it knows the closing, not the screen. */
    public reload(projectId: string): void {
        this.store.dispatch(closingAdjustmentActions.reloadAdjustments({ projectId }));
    }

    public setFilter(filter: AdjustmentFilterKey): void {
        this.store.dispatch(closingAdjustmentActions.setFilter({ filter }));
    }

    public setFiscalYear(fiscalYear: number): void {
        this.store.dispatch(closingAdjustmentActions.setFiscalYear({ fiscalYear }));
    }

    /** `null` is the third click — back to the server's order. */
    public setSort(sort: AdjustmentSort | null): void {
        this.store.dispatch(closingAdjustmentActions.setSort({ sort }));
    }

    public setSearchTerm(searchTerm: string): void {
        this.store.dispatch(closingAdjustmentActions.setSearchTerm({ searchTerm }));
    }

    public loadAdjustment(projectId: string, adjustmentId: string): void {
        this.store.dispatch(closingAdjustmentActions.loadAdjustment({ projectId, adjustmentId }));
    }

    public clearAdjustment(): void {
        this.store.dispatch(closingAdjustmentActions.clearAdjustment());
    }

    /** Enter a correction by hand — the only way one comes into existence (§6). */
    public createAdjustment(projectId: string, request: ICreateAdjustmentRequest): void {
        this.store.dispatch(closingAdjustmentActions.createAdjustment({ projectId, request }));
    }

    /** Edit an OPEN correction. The backend refuses anything else (§7). */
    public editAdjustment(projectId: string, adjustmentId: string, request: IEditAdjustmentRequest): void {
        this.store.dispatch(closingAdjustmentActions.editAdjustment({ projectId, adjustmentId, request }));
    }

    /** Discard an OPEN correction — a STATE, never a delete (§7). */
    /** `Verworfen` → `Freigegeben`. Refused by the server for a row that was already transferred. */
    public restoreAdjustment(projectId: string, adjustmentId: string): void {
        this.store.dispatch(closingAdjustmentActions.restoreAdjustment({ projectId, adjustmentId }));
    }

    public discardAdjustment(projectId: string, adjustmentId: string): void {
        this.store.dispatch(closingAdjustmentActions.discardAdjustment({ projectId, adjustmentId }));
    }

    /** Transfer a SELECTION with one of three targets (§3). */
    public transferAdjustments(
        projectId: string,
        adjustmentIds: string[],
        method: AdjustmentTransferMethod,
    ): void {
        this.store.dispatch(closingAdjustmentActions.transferAdjustments({ projectId, adjustmentIds, method }));
    }

    public clearTransferResult(): void {
        this.store.dispatch(closingAdjustmentActions.clearTransferResult());
    }

    /**
     * Resolve an AMBIGUOUS journal match (§4, AC7).
     *
     * ⚠️ The only method here that leads to `Gebucht`, and it does not break §2 — it confirms it:
     * `journalEntryId` has to be one the reconciliation served, and the backend refuses anything else.
     */
    public resolveAmbiguity(projectId: string, adjustmentId: string, journalEntryId: string): void {
        this.store.dispatch(
            closingAdjustmentActions.resolveAmbiguity({ projectId, adjustmentId, journalEntryId }),
        );
    }
}
